import * as PIXI from "pixi.js";
import gsap from "gsap";
import { Symbol } from "../Symbol";
import { WinCounter } from "../WinCounter";
import { moveObjectToNewParent } from "src/shared/util/UtilFunctions";
import {
  InitialResponse,
  SpinResponse,
  Win,
} from "src/shared/util/Types";
import { IReel, Reel, ReelState } from "src/Panel/Reel";
import { ReelSpin } from "src/Panel/ReelSpin";
import { GameEngine } from "../server/GameEngine";
import { GameAssets } from "src/shared/util/GameAssets";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import { Spine } from "@pixi/spine-pixi";
import { Payline } from "src/Panel/Payline";
import { soundManager } from "src/shared/UI/SoundManager";
import { SoundInstance } from "src/shared/UI/SoundManagerUtils";
import AppSingleton from "src/shared/util/AppSingleton";

const STOP_REEL_INTERVAL: number = 250;
const STOP_REEL_ANTICIPATION_INTERVAL: number = 3000;
const SPIN_REEL_INTERVAL: number = 30;
// Define an enum for the states
export enum PanelState {
  IDLE = "IDLE",
  SPIN = "SPIN",
  SPIN_END = "SPIN_END",
  MATCH = "MATCH",
  CASCADE = "CASCADE",
  EXPANDED_WILD = "EXPANDED_WILD",
  WIN = "WIN",
  STOPPING = "STOPPING",
  MULTIPLIER = "MULTIPLIER",
  SCATTER_MATCH = "SCATTER_MATCH",
  STOP = "STOP",
}

export class Panel extends PIXI.Container {
  private panelData!: string[][];
  private reels: IReel[] = [];
  private winLayer: PIXI.Graphics;
  private panelMask: PIXI.Graphics;
  private panelBack: PIXI.Sprite;
  private topContainer: PIXI.Container;
  private paylineContainer: PIXI.Container;
  private bottomContainer: PIXI.Container;
  private currentState: PanelState = PanelState.STOP;
  private winCounter!: WinCounter;
  private content: PIXI.Container;
  private currentReelIndex: number = 0;
  private currentSccatersAmount: number = 0;
  private isQuickStop: boolean = false;
  private data!: SpinResponse;
  private stopNextId!: NodeJS.Timeout | null;
  private roundWin: number = 0;
  private logoSkeleton: Spine;
  private winText: PIXI.Text;
  private winTextFlickerTween: GSAPTween | undefined;
  private winTextPulseTween: GSAPTween | undefined;

  private reelSpinLoop: SoundInstance | undefined = new SoundInstance();
  private anticipationSoundInstance: SoundInstance | undefined = new SoundInstance();
  private bulbsSkeleton: Spine;

  private currentLoopId: number = 0;
  private isSpinning: boolean = false; // To stop the loop when a new spin starts

  constructor() {
    super();

    this.bottomContainer = new PIXI.Container();
    this.topContainer = new PIXI.Container();

    this.content = new PIXI.Container();
    this.addChild(this.content);

    this.panelBack = new PIXI.Sprite(
      GameAssets.textures.panel
    );
    this.panelBack.anchor.set(0.5);
    this.panelBack.scale.set(1.05);
    this.panelBack.position.set(0, 160);

    this.logoSkeleton = Spine.from({
      skeleton: "logoJson",
      atlas: "logoAtlas",
    });

    this.logoSkeleton.state.setAnimation(0, 'idle', true);

    const width: number = Symbol.SYMBOL_WIDTH * GameEngine.getInstance().config.reels;

    this.paylineContainer = new PIXI.Container();

    this.panelMask = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows + 5, // Adjusted height to fit the panel
      )
      .fill(0xffffff);
    const paylineMask = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows,
      )
      .fill(0xffffff);
    this.bottomContainer.mask = this.panelMask;
    this.bottomContainer.addChild(this.panelMask);

    this.paylineContainer.mask = paylineMask;
    this.paylineContainer.addChild(paylineMask);

    this.winLayer = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows,
      )
      .fill(0x000000);
    this.winLayer.alpha = 0.7;

    this.winLayer.visible = false;
    this.bottomContainer.position.set(0, -148);
    this.winLayer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );
    this.topContainer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );
    this.paylineContainer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );

    this.winCounter = new WinCounter();
    this.winCounter.zIndex = 10;

    this.bulbsSkeleton = Spine.from({
      skeleton: "bulbsJson",
      atlas: "bulbsAtlas",
    });
    this.bulbsSkeleton.position.set(0, 180);
    this.bulbsSkeleton.scale.set(0.25);
    this.bulbsSkeleton.state.setAnimation(0, "on", true);

    // ----------- The Dot Matrix Display Grid ----------- //

    // 1. Create a Container to hold the "Electronic" parts
    const displayContainer = new PIXI.Container();
    displayContainer.position.set(0, -232);

    this.content.addChild(displayContainer);

    // 2. Create the "Stencil" (The shape of your display)
    // Use a Rounded Rectangle or a custom shape that matches your cylindrical hole
    const stencil = new PIXI.Graphics()
      .roundRect(-240, -32, 480, 65, 48) // Match your panel's display dimensions
      .fill({ color: 0xffffff });
    stencil.position.set(displayContainer.x, displayContainer.y);
    this.content.addChild(stencil);
    displayContainer.mask = stencil; // Everything in displayContainer is now trapped inside this shape

    // 3. Add the Text
    this.winText = new PIXI.Text(
      {
        text: "1,501.00",
        style: {
          fontSize: 70,
          fontFamily: "LcdFont",
          align: "center",
          fill: 0xffcc00,
          dropShadow: { color: 0xffcc00, blur: 10, distance: 0, alpha: 0.8 },
          letterSpacing: 4
        }
      });
    this.winText.anchor.set(0.5);
    displayContainer.addChild(this.winText);

    // 4. Add the Grid Overlay (Trapped inside the mask)
    const g = new PIXI.Graphics()
      .rect(2, 0, 1, 1).fill({ color: 0x000000, alpha: 0.2 })
      .rect(0, 2, 3, 1).fill({ color: 0x000000, alpha: 0.2 });

    const gridTexture = AppSingleton.getInstance().app.renderer.textureGenerator.generateTexture(g);
    const pixelGrid = new PIXI.TilingSprite({
      texture: gridTexture,
      width: 2000, // Make it wider than the display
      height: 200,
    });
    pixelGrid.anchor.set(0.5);
    pixelGrid.blendMode = 'multiply';
    pixelGrid.x = 0.01;
    pixelGrid.y = 0.895;
    displayContainer.addChild(pixelGrid);

    // 5. THE CYLINDER TRICK: Bulge the text
    // To simulate the curve, we subtly expand the center
    this.winText.scale.set(1.1, 1.05); // Slightly wider and taller to feel "projected"    pixelGrid.blendMode = 'multiply';

    // ---------------------------------------------- //

    // DMD flicker 
    this.winTextFlickerTween = gsap.to(this.winText, {
      alpha: 0.85,
      duration: 0.04,
      repeat: -1,
      yoyo: true,
      ease: "none"
    });

    this.winTextPulseTween = gsap.to(displayContainer, {
      alpha: 0.2,
      duration: 1.5,
      repeat: -1,
      ease: "power4.in",
      yoyo: true
    });

    this._changeState(PanelState.IDLE);

    this.content.addChild(this.panelBack);
    this.content.addChild(this.bottomContainer);
    this.content.addChild(this.winLayer);
    this.content.addChild(this.topContainer);
    this.content.addChild(this.paylineContainer);
    this.content.addChild(this.bulbsSkeleton);
    this.content.addChild(this.logoSkeleton);
    this.content.addChild(this.winCounter);
    this.content.addChild(displayContainer);

    this.setWinVisible(false);

    // for (let i = 1; i <= 10; i++) {
    //     const payline: Payline = Payline.pool.acquire(i);
    //     payline.zIndex = 20;
    //     payline.y = 140;
    //     payline.scale.set(1.15);
    //     this.paylineContainer.addChild(payline);
    // }
  }

  public setWinVisible(visible: boolean) {
    this.winText.visible = visible;

    if (visible) {
      this.winTextFlickerTween?.play();
      this.winTextPulseTween?.play();
    } else {
      this.winTextFlickerTween?.pause();
      this.winTextPulseTween?.pause();
    }
  }

  public init(data: InitialResponse) {
    this.panelData = data.panel;
    this._createReels();
  }

  public onLandscape(data: any) {
    this.scale.set(1.3);
    this.y = -100;

    const k = Math.max(1.7 / data.ratio, 1) * 1.1;
    this.logoSkeleton.position.set(-600 / k, -220);
    this.logoSkeleton.scale.set(0.13 / k);
    this.winCounter.onLandscape();
  }

  public onPortrait(data: any) {
    this.y = -150;
    const maxRatio = 0.77;
    const maxScale = 1.6;
    const ratio = data.ratio;

    let scale: number;
    if (ratio >= maxRatio) {
      scale = maxScale;
      this.logoSkeleton.scale.set(0.1);
      this.logoSkeleton.position.set(0, -360);
    } else {
      // Linear interpolation between minScale and maxScale
      scale = ratio / maxRatio * maxScale;

      this.logoSkeleton.scale.set(0.1 / (ratio / maxRatio));
      // Use a coefficient to control the speed of y position change
      const yCoef = 1.3; // Adjust this coefficient to control the speed
      const y = -360 - ((1 - (ratio / maxRatio)) * 200 * yCoef);
      this.logoSkeleton.position.set(0, y);
    }

    this.scale.set(scale);


    this.winCounter.onPortrait();
  }

  public getState(): PanelState {
    return this.currentState;
  }

  public async setData(data: SpinResponse) {
    this.roundWin = 0;
    this.data = data;
    this.roundWin = this.data.roundWin;

    this.panelData = JSON.parse(JSON.stringify(data.panel));
    this.reels.forEach((reel, index) => {
      const reelData = this.panelData[index];
      reel.setData(reelData);
    });

    this._stopReels();
  }

  private _getWildByCoordinate(reel: number, row: number): Symbol | null {
    return (
      (this.bottomContainer.children.find(
        (child) =>
          child instanceof Symbol &&
          child.position.x ===
          reel * Symbol.SYMBOL_WIDTH - 2 * Symbol.SYMBOL_WIDTH &&
          child.position.y === (row + 0.5) * Symbol.SYMBOL_HEIGHT,
      ) as Symbol) || null
    );
  }

  private _stopReels() {
    if (this.panelData.length && this.currentState === PanelState.SPIN_END) {
      this._changeState(PanelState.STOPPING);
    }
  }

  private _createReels() {
    for (let i = 0; i < GameEngine.getInstance().config.reels; i++) {
      const reel = new ReelSpin(this.panelData[i], i);
      reel.x =
        i * Symbol.SYMBOL_WIDTH -
        (GameEngine.getInstance().config.reels * Symbol.SYMBOL_WIDTH) / 2 +
        Symbol.SYMBOL_WIDTH / 2;
      this.bottomContainer.addChild(reel);
      this.reels.push(reel);
      reel.on(ReelState.IDLE, this._onReelStop, this);
      reel.on(ReelState.SPIN_END, this._onReelSpinEnd, this);
    }
  }

  private _onReelStop() {
    let allStopped: boolean = true;
    this.reels.forEach((reel: IReel) => {
      if (reel.getState() !== ReelState.IDLE) {
        allStopped = false;
      }
    });
    if (allStopped) {
      if (this.reelSpinLoop) {
        soundManager.stopSoundInstance(this.reelSpinLoop!);
        this.reelSpinLoop = undefined;
      }

      this.isQuickStop = false;
      this._changeState(PanelState.STOP);
    }
  }

  private _onReelSpinEnd(reel: Reel) {
    let allSpin: boolean = true;
    this.reels.forEach((reel: IReel) => {
      if (reel.getState() !== ReelState.SPIN_END) {
        allSpin = false;
      }
    });
    if (allSpin) {
      this._changeState(PanelState.SPIN_END);
      this._stopReels();
    }
  }

  // Method to change the state and execute the corresponding actions
  private _changeState(newState: PanelState) {
    if (this.currentState === newState) {
      return; // No change if the same state
    }

    // Enter logic for the new state
    this.currentState = newState;
    switch (this.currentState) {
      case PanelState.IDLE:
        this._onEnterIdle();
        break;
      case PanelState.SPIN:
        this._onEnterSpin();
        break;
      case PanelState.WIN:
        this._onRoundWin();
        break;
      case PanelState.MATCH:
        (async () => {
          await this._onMatch();
        })();
        break;
      case PanelState.SCATTER_MATCH:
        (async () => await this._onScatterMatch())();
        soundManager.playSound("wildBonus");
        break;
      case PanelState.EXPANDED_WILD:
        this._onExpandedWilds();
        break;
      case PanelState.STOPPING:
        this._onStopping();
        break;
      case PanelState.STOP:
        this._changeState(PanelState.EXPANDED_WILD);

        console.log("stopping anticipation sound");
        setTimeout(() => {
          if (this.anticipationSoundInstance) {
            soundManager.stopSoundInstance(this.anticipationSoundInstance);
            this.anticipationSoundInstance = undefined;
          }
        }, 100);

        break;
    }

    this.emit(this.currentState);
  }

  // Define custom logic for entering and exiting each state
  private _onEnterIdle() {
    this.bulbsSkeleton.state.setAnimation(0, "off", true);
  }

  private _resetQuickStop() {
    this.isQuickStop = false;
    this.emit("resetQuickStop");
  }

  private async _onEnterSpin() {
    this.isQuickStop = false;
    this.isSpinning = true;
    this.currentLoopId++;

    // 1. SAFE RECOVERY: Copy the array before iterating so we don't skip children
    const symbolsToRecover = [...this.topContainer.children];

    symbolsToRecover.forEach((child) => {
      if (child instanceof Symbol) {
        // Force return to reel
        if (child.originalParent) {
          moveObjectToNewParent(child, child.originalParent);
          child.position.set(child.originalPosition.x, child.originalPosition.y);
        } else {
          // Fallback: If parent is lost, release to pool so it doesn't just hang in topContainer
          this.topContainer.removeChild(child);
        }
      }
    });

    // 2. Clear visual layers
    this.topContainer.removeChildren();
    this.paylineContainer.removeChildren();
    this.winLayer.visible = false;
    this.setWinVisible(false);

    // 3. Reset Reels - MUST happen AFTER symbol recovery
    for (let i = 0; i < this.reels.length; i++) {
      this.reels[i].resetData();
      // Ensure the reel itself is in the bottom container
      if (this.reels[i].parent !== this.bottomContainer) {
        moveObjectToNewParent(this.reels[i] as any, this.bottomContainer);
      }
    }

    // ... rest of spin logic
    this.currentReelIndex = 0;
    this.reels[this.currentReelIndex].triggerSpin();
    this.currentReelIndex++;
    this._startNextSpin();
    this.reelSpinLoop = await soundManager.playSound("reelSpinLoop");
  }


  private _startNextSpin() {
    this.stopNextId = setTimeout(() => {
      if (this.reels[this.currentReelIndex]) {
        this.reels[this.currentReelIndex].triggerSpin();
        this.currentReelIndex++;
        this._startNextSpin();
      }
    }, SPIN_REEL_INTERVAL);
  }

  private async _onRoundWin() {
    this._resetQuickStop();
    this.isSpinning = false;

    await this.winCounter.showPrize(this.roundWin, this.data.odd);
    this.winText.text = GameEngine.getInstance().getFormatter().format(this.roundWin);
    this.setWinVisible(true);

    if (this.data.scatters && this.data.scatters.coordinates.length) {
      await this._onScatterMatch();
    }

    if (this.data.wins.length > 0) {
      // IMPORTANT: Start loop but DON'T await it, or the state never changes
      this._startWinLoop(0, this.currentLoopId); // Pass the ID here
      // Allow the UI to let the player spin again
      this._changeState(PanelState.IDLE);
    } else {
      this._changeState(PanelState.IDLE);
    }
  }

  private async _startWinLoop(startIndex: number = 0, loopId: number) {
    // Immediate exit if loop is stale
    if (this.isSpinning || loopId !== this.currentLoopId) return;

    const winData = this.data.wins[startIndex];
    this.winLayer.visible = true;

    const { symbols, paylines } = await this._showSingleWin(winData);

    // After an 'await', the world has changed. Check validity again.
    if (loopId !== this.currentLoopId) {
      paylines.forEach(p => Payline.pool.release(p));
      // DO NOT move symbols here. _onEnterSpin handled it.
      return;
    }

    await new Promise(res => setTimeout(res, 1200));

    // After the pause, check validity again.
    if (loopId !== this.currentLoopId) {
      paylines.forEach(p => Payline.pool.release(p));
      return;
    }

    // Normal cleanup for the next iteration of the loop
    paylines.forEach(p => Payline.pool.release(p));
    symbols.forEach(s => {
      // ONLY move back if we are still in the win loop and NOT spinning
      if (loopId === this.currentLoopId && s.parent === this.topContainer) {
        moveObjectToNewParent(s, s.originalParent);
        s.position.set(s.originalPosition.x, s.originalPosition.y);
      }
    });

    const nextIndex = (startIndex + 1) % this.data.wins.length;
    this._startWinLoop(nextIndex, loopId);
  }

  private async _showSingleWin(winData: Win): Promise<{ symbols: Symbol[], paylines: Payline[] }> {
    const winningSymbols: Symbol[] = [];
    const activePaylines: Payline[] = [];

    winData.coordinates.forEach(coord => {
      const reelObj = this.reels[coord.reel];
      const symbol = reelObj.hasExpandedWild() ? reelObj.getSymbol(0) : reelObj.getSymbol(coord.row);
      if (symbol) {
        winningSymbols.push(symbol);
        symbol.originalParent = symbol.parent as PIXI.Container;
        symbol.originalPosition = new PIXI.Point(symbol.x, symbol.y);
        moveObjectToNewParent(symbol, this.topContainer);
      }
    });

    if (typeof winData.payline !== "undefined" && winData.payline >= 0) {
      const payline = Payline.pool.acquire(winData.payline + 1);
      // Reset positioning logic: if container is at -148, 148 is the center.
      payline.position.set(0, 148);
      this.paylineContainer.addChild(payline);
      activePaylines.push(payline);
      // @ts-ignore
      payline.skeleton.state.setAnimation(0, `Payline${winData.payline + 1}`, false);
    }

    await Promise.all(winningSymbols.map(s => s.win(this.isQuickStop)));

    // DO NOT move symbols back here. Return them so the loop can clean them up.
    return { symbols: winningSymbols, paylines: activePaylines };
  }

  private async _onMatch(): Promise<void> {
    this.winLayer.visible = true;
    const allWinningSymbols = new Set<Symbol>();

    this.data.wins.forEach(win => {
      win.coordinates.forEach(coord => {
        // Correctly access the array of coordinates
        const reelObj = this.reels[coord.reel];
        const symbol = reelObj.hasExpandedWild() ?
          reelObj.getSymbol(0) :
          reelObj.getSymbol(coord.row);
        if (symbol) allWinningSymbols.add(symbol);
      });
    });

    allWinningSymbols.forEach(s => moveObjectToNewParent(s, this.topContainer));

    await soundManager.playSound("paylineShow");
    await soundManager.playSound("matchShow");

    await Promise.all([
      Promise.all(this.data.wins.map((winData) => this._showWin(winData))),
      new Promise(res => setTimeout(res, 1200))
    ]);

    // Return to original reel and FORCE SNAP
    allWinningSymbols.forEach(s => {
      moveObjectToNewParent(s, s.originalParent);
      // This is the safety "Snap". It forces the symbol to ignore any 
      // lingering tween offsets and sit exactly on the grid.
      const row = Math.round((s.y - Symbol.SYMBOL_HEIGHT / 2) / Symbol.SYMBOL_HEIGHT);
      s.y = Symbol.SYMBOL_HEIGHT * row + Symbol.SYMBOL_HEIGHT / 2;
    });

    this.winLayer.visible = false;
    this._changeState(PanelState.WIN);
  }

  private async _onScatterMatch(): Promise<void> {
    if (this.data.scatters && this.data.scatters.coordinates.length) {
      this.winLayer.visible = true; // Layer ON
      await this._showWin(this.data.scatters);
      this.winLayer.visible = false; // Layer OFF
    }

    this._changeState(PanelState.IDLE);
  }

  private async _onExpandedWilds(): Promise<void> {
    if (this.data.wins.length) {
      await this._expandWilds();
      this._changeState(PanelState.MATCH);
    } else if (
      this.data.scatters &&
      this.data.scatters.coordinates.length
    ) {
      this._changeState(PanelState.SCATTER_MATCH);
    } else {
      this._changeState(PanelState.IDLE);
    }
  }

  private _expandWilds(): Promise<void> {
    return new Promise<void>(async (resolve) => {
      // Call expandWild on all reels and await their promises
      await Promise.all(this.reels.map(reel => reel.expandWild()));
      resolve();
    });
  }

  private async _showWin(winData: Win) {
    const mult: number = winData.win / GlobalState.getInstance().bet;
    if (mult < 3) {
      this.bulbsSkeleton.state.setAnimation(0, "fall1", false);
      this.bulbsSkeleton.state.addAnimation(0, "fall1", false);
    } else if (mult < 10) {
      this.bulbsSkeleton.state.setAnimation(0, "fall2", false);
      this.bulbsSkeleton.state.addAnimation(0, "fall2", false);
    } else if (mult < 20) {
      this.bulbsSkeleton.state.setAnimation(0, "flicker", false);
      this.bulbsSkeleton.state.addAnimation(0, "flicker", false);
    } else {
      this.bulbsSkeleton.state.setAnimation(0, "checker", false);
      this.bulbsSkeleton.state.addAnimation(0, "checker", false);
    }

    this.bulbsSkeleton.state.addAnimation(0, "on", true);
    this.bulbsSkeleton.state.setAnimation(0, "checker", true);

    const paylines: Payline[] = [];

    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol = this.reels[coordinates.reel].hasExpandedWild() ?
        this.reels[coordinates.reel].getSymbol(0) : this.reels[coordinates.reel].getSymbol(coordinates.row);

      if (typeof winData.payline !== "undefined" && winData.payline >= 0) {
        const payline: Payline = Payline.pool.acquire(winData.payline + 1);
        payline.zIndex = 20;
        payline.y = 140;
        payline.scale.set(1.15);
        this.paylineContainer.addChild(payline);
        paylines.push(payline);

      }
      return await symbol.win(this.isQuickStop);
    });

    await Promise.all(winPromises); // Wait for all win() calls to complete

    paylines.forEach((payline) => {
      Payline.pool.release(payline);
    });
  }

  private async _onStopping() {
    this.currentReelIndex = 0;
    this.currentSccatersAmount = 0;
    if (this.stopNextId) {
      clearTimeout(this.stopNextId);
    }
    if (this.isQuickStop) {
      if (this.reelSpinLoop) {
        soundManager.stopSoundInstance(this.reelSpinLoop!);
        this.reelSpinLoop = undefined;
      }

      for (let i = this.currentReelIndex; i < this.reels.length; i++) {
        this.reels[i].triggerStop(this.isQuickStop);
      }
    } else {
      this._stopNext();
    }
  }

  private _triggerStopReel(delay: number = STOP_REEL_INTERVAL) {
    this.reels[this.currentReelIndex].triggerStop(this.isQuickStop);
    this.currentReelIndex++;
    this.stopNextId = setTimeout(async () => {
      await soundManager.playSound("reelStop");
      this._stopNext();
    }, delay);
  }

  private _stopNext() {
    if (this.isQuickStop) {
      if (this.stopNextId) {
        clearTimeout(this.stopNextId);
        this.stopNextId = null;
        for (let i = this.currentReelIndex; i < this.reels.length; i++) {
          this.reels[i].triggerStop(this.isQuickStop);
        }
      }
    } else if (this.reels[this.currentReelIndex]) {
      this.stopNextId = null;
      if (this._checkForAnticipation() && this.currentReelIndex === 4) {
        this.reels[this.currentReelIndex].startAnticipation();

        (async () => {
          this.anticipationSoundInstance = await soundManager.playSound("anticipation")
        })();

        if (this.reelSpinLoop) {
          soundManager.stopSoundInstance(this.reelSpinLoop!);
          this.reelSpinLoop = undefined;
        }

        // console.log("stopped anticipation");
        soundManager.stopSoundInstance(this.anticipationSoundInstance!);
        this.stopNextId = setTimeout(() => {
          this._triggerStopReel(5 * STOP_REEL_INTERVAL);
        }, STOP_REEL_ANTICIPATION_INTERVAL);
      } else {
        this._triggerStopReel();
      }
    }
  }

  private _checkForAnticipation(): boolean {
    // Safety check: if there's no data, there's no anticipation
    if (!this.data || !this.data.wins) {
      return false;
    }

    let hasAnticipation: boolean = false;
    this.data.wins.forEach((win) => {
      if (win.symbol === '1') {
        if (win.odd > 3) {
          hasAnticipation = true;
        }
      }
      if (win.symbol === '100') {
        if (win.odd > 3) {
          hasAnticipation = true;
        }
      }
      if (win.symbol === '101') {
        if (win.odd > 2) {
          hasAnticipation = true;
        }
      }
    });
    return hasAnticipation;
  }

  public spin() {
    this._changeState(PanelState.SPIN);
  }

  public async quickStop() {
    this.isQuickStop = true;
    console.log("QUICK STOP", this.currentState);

    switch (this.currentState) {
      case PanelState.SPIN:
      case PanelState.SPIN_END:
        break;
      case PanelState.STOPPING:
        this._onStopping();
        break;
      case PanelState.MATCH:
        break;
      case PanelState.WIN:
        this.winCounter.quickStop();
        break;
    }
  }
}
